import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import type { AppProps } from 'next/app';
import { AnimatePresence } from 'framer-motion';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Hydrate } from 'react-query/hydration';
import { ToastContainer } from 'react-toastify';
import { appWithTranslation } from 'next-i18next';
// import { ReactQueryDevtools } from "react-query/devtools";

import { DefaultSeo } from '@components/common/default-seo';
import { ManagedUIContext } from '@contexts/ui.context';
import ManagedModal from '@components/common/modal/managed-modal';
import ManagedDrawer from '@components/common/drawer/managed-drawer';

// Load Open Sans and satisfy typeface font
import '@fontsource/open-sans';
import '@fontsource/open-sans/600.css';
import '@fontsource/open-sans/700.css';
import '@fontsource/satisfy';
// external
import 'react-toastify/dist/ReactToastify.css';
// base css file
import '@styles/scrollbar.css';
import '@styles/swiper-carousel.css';
import '@styles/custom-plugins.css';
import '@styles/tailwind.css';
import '@styles/rc-drawer.css';
import { getDirection } from '@utils/get-direction';
import React from 'react';
import { EditPermissionProvider } from '@contexts/edit-permission';
import { getCurrentLocationAndLanguage } from '@utils/location';
import Cookies from 'js-cookie';
import { OrderProvider } from '@contexts/order-context';
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from '@components/layout/theme-provider';
function handleExitComplete() {
  if (typeof window !== 'undefined') {
    window.scrollTo({
      top: 0
    });
  }
}
function Noop({
  children
}: React.PropsWithChildren<{}>) {
  return <>{children}</>;
}
const CustomApp = ({
  Component,
  pageProps
}: AppProps) => {
  const queryClientRef = useRef<any>();
  if (!queryClientRef.current) {
    queryClientRef.current = new QueryClient();
  }
  const router = useRouter();
  const dir = getDirection(router.locale);
  useEffect(() => {
    document.documentElement.dir = dir;
  }, [dir]);
  const Layout = (Component as any).Layout || Noop;
  useEffect(() => {
    const {
      language
    } = getCurrentLocationAndLanguage();
    if (!Cookies.get('NEXT_LOCALE')) {
      Cookies.set('NEXT_LOCALE', language);
      router.push(router.asPath, undefined, {
        locale: language
      });
    }
  }, [router]);
  return <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange data-sentry-element="ThemeProvider" data-sentry-component="CustomApp" data-sentry-source-file="_app.tsx">
    <TooltipProvider data-sentry-element="TooltipProvider" data-sentry-source-file="_app.tsx">
    <OrderProvider data-sentry-element="OrderProvider" data-sentry-source-file="_app.tsx">
   <EditPermissionProvider data-sentry-element="EditPermissionProvider" data-sentry-source-file="_app.tsx">
    <AnimatePresence mode='wait' onExitComplete={handleExitComplete} data-sentry-element="AnimatePresence" data-sentry-source-file="_app.tsx">
      <QueryClientProvider client={queryClientRef.current} data-sentry-element="QueryClientProvider" data-sentry-source-file="_app.tsx">
        {/* @ts-ignore */}
        <Hydrate state={pageProps.dehydratedState} data-sentry-element="Hydrate" data-sentry-source-file="_app.tsx">
          {/* @ts-ignore */}
          <ManagedUIContext data-sentry-element="ManagedUIContext" data-sentry-source-file="_app.tsx">
            <Layout pageProps={pageProps} data-sentry-element="Layout" data-sentry-source-file="_app.tsx">
              <DefaultSeo data-sentry-element="DefaultSeo" data-sentry-source-file="_app.tsx" />
              <Component {...pageProps} key={router.route} data-sentry-element="Component" data-sentry-source-file="_app.tsx" />
              <ToastContainer data-sentry-element="ToastContainer" data-sentry-source-file="_app.tsx" />
            </Layout>
            <ManagedModal data-sentry-element="ManagedModal" data-sentry-source-file="_app.tsx" />
            <ManagedDrawer data-sentry-element="ManagedDrawer" data-sentry-source-file="_app.tsx" />
          </ManagedUIContext>
        </Hydrate>
        {/* <ReactQueryDevtools /> */}
      </QueryClientProvider>
    </AnimatePresence>
   </EditPermissionProvider>
   </OrderProvider>
   </TooltipProvider>
   </ThemeProvider>;
};
export default appWithTranslation(CustomApp);